import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AuthPage } from './components/AuthPage'
import { AuthenticatedLayout } from './components/AuthenticatedLayout'
import { NewsletterOptInModal } from './components/NewsletterOptInModal'
import { OnboardingModal } from './components/OnboardingModal'
import { ProductTour } from './components/ProductTour'
import { AuthProvider } from './contexts/AuthContext'
import { ApplicationsProvider } from './contexts/ApplicationsContext'
import { useAuth } from './hooks/useAuth'
import { useApplications } from './hooks/useApplications'
import { usePageTracking } from './hooks/usePageTracking'
import { subscribeToNewsletter } from './services/newsletterService'

// Public routes accessible without authentication (handled by React)
// Note: Landing (/), About, Pricing are now handled by Astro
const PUBLIC_ROUTES = ['/login', '/extension/callback'] as const

function App() {
  return (
    <AuthProvider>
      <ApplicationsProvider>
        <AppContent />
      </ApplicationsProvider>
    </AuthProvider>
  )
}

function AppContent() {
  const navigate = useNavigate()
  const location = useLocation()

  const {
    sessionUserId,
    sessionEmail,
    avatarUrl,
    isAuthReady,
    newsletterPromptEmail,
    dismissNewsletterPrompt,
    handleLogout,
  } = useAuth()

  const {
    settings,
    effectivePlan,
    isPro,
    isLoadingSettings,
    isSavingSettings,
    handleSaveSettings,
  } = useApplications()
  const [isProductTourOpen, setIsProductTourOpen] = useState(false)

  // Track page views on route changes (GA + Meta Pixel + document.title)
  usePageTracking()

  const isReturningFromPayment = location.pathname === '/billing' &&
    (location.search.includes('success=true') || location.search.includes('cancelled=true'))

  const startProductTour = () => {
    navigate('/', { replace: false })
    window.setTimeout(() => setIsProductTourOpen(true), 180)
  }

  // Redirect to login if not authenticated (except for public routes)
  useEffect(() => {
    if (!isAuthReady) {
      return
    }
    if (!sessionUserId && !PUBLIC_ROUTES.includes(location.pathname as typeof PUBLIC_ROUTES[number]) && !isReturningFromPayment) {
      // Clear stale SSR hint cookie and full-navigate so Astro serves the landing page
      document.cookie = 'jt_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
      window.location.href = '/'
    }
  }, [isAuthReady, sessionUserId, location.pathname, navigate, isReturningFromPayment])

  // Redirect to /pricing after login if user clicked "Get Pro" on landing page
  useEffect(() => {
    if (sessionUserId) {
      const hash = window.location.hash
      const hashMatch = hash.match(/^#plan=(monthly|quarterly|lifetime)$/)
      if (hashMatch) {
        const planParam = hashMatch[1] as 'monthly' | 'quarterly' | 'lifetime'
        window.history.replaceState({}, '', window.location.pathname + window.location.search)
        navigate(`/pricing?plan=${planParam}`, { replace: true })
      }
    }
  }, [sessionUserId, navigate])

  // Redirect to extension connect page when opened via extension
  useEffect(() => {
    if (sessionUserId) {
      const hash = window.location.hash
      if (hash === '#extension-connect') {
        window.history.replaceState({}, '', window.location.pathname + window.location.search)
        navigate('/extension/connect', { replace: true })
      }
    }
  }, [sessionUserId, navigate])

  // Handle redirect after login
  useEffect(() => {
    if (sessionUserId && location.pathname === '/login') {
      const params = new URLSearchParams(location.search)
      const redirectTo = params.get('redirect') || '/'
      navigate(redirectTo, { replace: true })
    }
  }, [sessionUserId, location.pathname, location.search, navigate])

  // Loading state
  if (!isAuthReady) {
    const isLogin = location.pathname === '/login'
    return (
      <div className={`min-h-screen flex items-center justify-center ${isLogin ? 'auth-page' : 'bg-app-page'}`}>
        <div className="text-center">
          <div className={`inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid ${isLogin ? 'auth-spinner' : 'border-sky-500 border-r-transparent'}`}></div>
          <p className={`mt-4 text-sm ${isLogin ? 'auth-muted' : 'text-app-muted'}`}>Loading the app...</p>
        </div>
      </div>
    )
  }

  // Payment return loading state
  if (!sessionUserId && isReturningFromPayment) {
    return (
      <div className="min-h-screen bg-app-page flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-sky-500 border-r-transparent"></div>
          <p className="mt-4 text-sm text-app-muted">Loading your account...</p>
        </div>
      </div>
    )
  }

  // Login page (for unauthenticated users)
  if (!sessionUserId && location.pathname === '/login') {
    return <AuthPage />
  }

  // Not authenticated and not on login - will redirect via useEffect
  if (!sessionUserId) {
    return (
      <div className="min-h-screen bg-app-page flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-sky-500 border-r-transparent"></div>
          <p className="mt-4 text-sm text-app-muted">Redirecting...</p>
        </div>
      </div>
    )
  }

  // Authenticated - show app
  return (
    <>
      <AuthenticatedLayout
        avatarUrl={avatarUrl}
        displayName={settings.displayName}
        email={sessionEmail}
        isPro={isPro}
        effectivePlan={effectivePlan}
        onLogout={handleLogout}
        onStartProductTour={startProductTour}
      />
      <OnboardingModal
        isOpen={!isLoadingSettings && !settings.onboardingCompleted}
        email={sessionEmail}
        initialPreferences={{
          goal: settings.onboardingGoal,
          currentSearchStage: settings.currentSearchStage,
          autoGhostEnabled: settings.autoGhostEnabled,
          autoArchiveEnabled: settings.autoArchiveEnabled,
          dateFormat: settings.dateFormat,
          themeMode: settings.themeMode,
          newsletterOptIn: true,
        }}
        isSaving={isSavingSettings}
        onComplete={async (preferences, startTour) => {
          await handleSaveSettings({
            ...settings,
            onboardingGoal: preferences.goal,
            currentSearchStage: preferences.currentSearchStage,
            autoGhostEnabled: preferences.autoGhostEnabled,
            autoArchiveEnabled: preferences.autoArchiveEnabled,
            dateFormat: preferences.dateFormat,
            themeMode: preferences.themeMode,
            onboardingCompleted: true,
          })
          if (preferences.newsletterOptIn && sessionEmail) {
            subscribeToNewsletter(sessionEmail).catch((err) => {
              console.error('Newsletter subscription failed during onboarding', err)
            })
          }
          if (startTour) {
            startProductTour()
          }
        }}
      />
      <ProductTour
        isOpen={isProductTourOpen}
        onClose={(completed) => {
          setIsProductTourOpen(false)
          if (completed && !settings.productTourCompleted) {
            void handleSaveSettings({ ...settings, productTourCompleted: true })
          }
        }}
      />
      <NewsletterOptInModal
        email={newsletterPromptEmail}
        isOpen={Boolean(newsletterPromptEmail) && settings.onboardingCompleted && !isProductTourOpen}
        onClose={dismissNewsletterPrompt}
      />
    </>
  )
}

export default App
